import { Title, Text, Group, Stack, Stepper, PasswordInput, Grid } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RecoveryCodesMethod } from '@extrahorizon/javascript-sdk';
import { useUserData } from 'hooks/api/useUser';
import { PortalButton } from 'components/Button/PortalButton';
import { validateRequired } from 'utils/validation';
import { useDrawerContext } from 'context/hooks';
import { useRegenerateCodes } from 'hooks/api/user/mfa/useRegenerateCodes';

export const RecoveryCodesForm = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const { data: userData } = useUserData();
  const { closeDrawer } = useDrawerContext();

  const passwordForm = useForm({
    initialValues: {
      password: '',
    },
    validate: {
      password: (value) => validateRequired({ value, label: t('password_label') }),
    },
  });

  const { mutate: regenerateCodes, isPending: isLoading } = useRegenerateCodes();

  const handleNextStep = () => {
    regenerateCodes({
      userId: userData?.user.id,
      password: passwordForm.values.password,
      callback: (result: RecoveryCodesMethod) => {
        setActiveStep(activeStep + 1);
        setRecoveryCodes(result.codes);
      },
    });
  };

  const buttonGroup = (confirmText: string) => {
    return (
      <Group mt={'xl'} justify={'flex-end'}>
        <PortalButton
          variant={'outline'}
          onClick={() => closeDrawer()}
          disabled={isLoading}
          label={t('cancel_label')}
        />
        <PortalButton type={'submit'} loading={isLoading} label={confirmText} />
      </Group>
    );
  };

  return (
    <Stack gap={'md'}>
      <Title order={3}>{t('mfa_recovery_title')}</Title>
      <Stepper color={'var(--mantine-color-turquoise-5)'} size={'sm'} active={activeStep}>
        <Stepper.Step label={t('mfa_enable_step_one')} description={t('mfa_enable_step_one_description')}>
          <Stack>
            <Text>{t('mfa_description')}</Text>
            <form onSubmit={passwordForm.onSubmit(handleNextStep)}>
              <PasswordInput placeholder={t('password_label')} {...passwordForm.getInputProps('password')} />
              {buttonGroup(t('continue_label'))}
            </form>
          </Stack>
        </Stepper.Step>
        <Stepper.Completed>
          <Stack gap={'xs'} mb={'md'}>
            <Text>{t('mfa_recovery_paragraph_one')}</Text>
            <Text>
              <Trans i18nKey={'mfa_recovery_paragraph_two'} />
            </Text>
            <Text>
              <Trans i18nKey={'mfa_recovery_paragraph_three'} />
            </Text>
          </Stack>
          <Grid justify={'center'}>
            {recoveryCodes?.map((code) => (
              <Grid.Col key={`grid-col-${code}`} span={6}>
                <Text>{code}</Text>
              </Grid.Col>
            ))}
          </Grid>
        </Stepper.Completed>
      </Stepper>
    </Stack>
  );
};
